import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const TIMEFRAME_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 28, label: 'Last 28 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
];

const VALUE_LINE_COLORS = ['#2e7d32', '#ed6c02', '#9c27b0'];

const SyncLineChart = ({ data, lines, height = 400, hideLegend = false }) => (
  <Box sx={{ width: '100%', height }}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="dateLabel" />
        <YAxis />
        <Tooltip />
        {!hideLegend && <Legend />}
        {lines.map((line) => (
          <Line
            key={line.dataKey}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.color}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </Box>
);

const CampaignDailySyncChartView = ({
  campaign,
  days,
  onDaysChange,
  onBack,
}) => {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState([]);
  const [selectedValueKeys, setSelectedValueKeys] = useState([]);

  useEffect(() => {
    if (!campaign?.id || !apiCall) return;

    let cancelled = false;

    const loadChartData = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await apiCall(
          'get',
          `/api/campaigns/${campaign.id}/daily-sync-totals?days=${days}`
        );

        if (cancelled) return;

        if (!result.success) {
          setError(result.error || 'Failed to load sync chart data');
          setChartData([]);
        } else {
          const rows = (result.data?.data || []).map((row) => ({
            ...row,
            dateLabel: dayjs(row.snapshot_date).format('MMM D'),
          }));
          setChartData(rows);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load sync chart data');
          setChartData([]);
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    loadChartData();

    return () => {
      cancelled = true;
    };
  }, [campaign?.id, days, apiCall]);

  const showValue1 = campaign.show_value_1 !== false;
  const showValue2 = campaign.show_value_2 !== false;
  const showValue3 = campaign.show_value_3 !== false;

  const valueLines = [
    showValue1 && { dataKey: 'value_1', name: campaign.value_1_label || 'Value 1', color: VALUE_LINE_COLORS[0] },
    showValue2 && { dataKey: 'value_2', name: campaign.value_2_label || 'Value 2', color: VALUE_LINE_COLORS[1] },
    showValue3 && { dataKey: 'value_3', name: campaign.value_3_label || 'Value 3', color: VALUE_LINE_COLORS[2] },
  ].filter(Boolean);

  useEffect(() => {
    const keys = [
      showValue1 && 'value_1',
      showValue2 && 'value_2',
      showValue3 && 'value_3',
    ].filter(Boolean);
    setSelectedValueKeys(keys);
  }, [campaign?.id, showValue1, showValue2, showValue3]);

  const toggleValueKey = (dataKey) => {
    setSelectedValueKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((key) => key !== dataKey)
        : [...prev, dataKey]
    );
  };

  const visibleValueLines = valueLines.filter((line) =>
    selectedValueKeys.includes(line.dataKey)
  );

  const pointsLines = [
    {
      dataKey: 'points',
      name: 'Points',
      color: '#d32f2f',
    },
  ];

  const loginsLines = [
    {
      dataKey: 'logins',
      name: 'Users logins',
      color: '#1565c0',
    },
  ];

  const emailsLines = [
    {
      dataKey: 'emails_sent',
      name: 'Emails sent',
      color: '#2e7d32',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mr: 2 }}>
            Back to Campaigns
          </Button>
          <Typography variant="h5" component="div">
            {campaign.name} — Sync Chart
          </Typography>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={days}
              label="Timeframe"
              onChange={(e) => onDaysChange(Number(e.target.value))}
            >
              {TIMEFRAME_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : chartData.length === 0 && !error ? (
          <Alert severity="info">
            No sync snapshots for this period. Daily snapshots are created on the first scheduled sync each day.
          </Alert>
        ) : (
          <Box>
            {valueLines.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Values
                </Typography>
                <SyncLineChart
                  data={chartData}
                  lines={visibleValueLines}
                  hideLegend={valueLines.length > 1}
                />
                {valueLines.length > 1 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 1 }}>
                    {valueLines.map((line) => (
                      <FormControlLabel
                        key={line.dataKey}
                        control={
                          <Checkbox
                            checked={selectedValueKeys.includes(line.dataKey)}
                            onChange={() => toggleValueKey(line.dataKey)}
                            sx={{
                              color: line.color,
                              '&.Mui-checked': { color: line.color },
                            }}
                          />
                        }
                        label={line.name}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Typography variant="h6" sx={{ mb: 2 }}>
              Points
            </Typography>
            <SyncLineChart data={chartData} lines={pointsLines} />

            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
              Users logins
            </Typography>
            <SyncLineChart data={chartData} lines={loginsLines} />

            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
              Emails sent
            </Typography>
            <SyncLineChart data={chartData} lines={emailsLines} />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default CampaignDailySyncChartView;
